import pictogramAsset from "assets/img/pictogram/asset.svg"

import { FC } from "react"
import { Link } from "react-router-dom"
import { Trans, useTranslation } from "react-i18next"

import ExistingLmidData from "models/ExistingLmidData"
import Lmid from "components/Lmid/Lmid"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import { Flex, FlexItem } from "components/Flex"
import { Grid } from "components/Grid"
import Text from "components/Text/Text"
import Button from "components/Button/Button"
import LmidModalResultItem from "../LmidModalResultItem"
import Routes from "@/routes"
import { IApiLink, Lmid as TLmid } from "@/types"

export interface INotUsedLmidData {
    lmid: TLmid
    isLabeled?: boolean
    drawingNumber?: string
    serialNumber?: string
    constructionYear?: number
}

interface ILmidModalNotUsedProps {
    existingLmidData: ExistingLmidData
    isInternalArticle: boolean
    onNotUsedLmidSelect(
        data: INotUsedLmidData,
        createAssetLink: IApiLink,
    ): void
    onSelectArticleClick(): void
    onClose(): void
}

const LmidModalNotUsed: FC<ILmidModalNotUsedProps> = ({
    existingLmidData,
    isInternalArticle,
    onNotUsedLmidSelect,
    onSelectArticleClick,
    onClose,
}) => {
    const { t } = useTranslation()

    function handleNotUsedLmidSelect() {
        if (!existingLmidData) return
        onNotUsedLmidSelect({
            lmid: existingLmidData.lmid,
            isLabeled: existingLmidData.isLabeled,
            drawingNumber: isInternalArticle ? existingLmidData.cmmAsset?.drawingNumber : undefined,
            serialNumber: isInternalArticle ? existingLmidData.cmmAsset?.serialNumber : undefined,
            constructionYear: isInternalArticle ? existingLmidData.cmmAsset?.constructionYear : undefined,
        }, existingLmidData.createAssetLink!)
        onClose()
    }

    return (
        <div className="py-4 text-center">
            {!!existingLmidData.cmmAsset && isInternalArticle &&
                <div className="text-center mb-3">
                    {t('lmidModal.doesSelectedArticleCorrespondsDisplayedInfo')}
                </div>
            }
            {!!existingLmidData.cmmAsset && !isInternalArticle &&
                <div className="text-center mb-3">
                    <Trans i18nKey="lmidModal.lmidFitsLiebherrAsset">
                        The LMID you have entered fits to an asset manufactured by Liebherr. Please select a different article or check whether you have entered the correct LMID. If you think this is a mistake please <Link to={Routes.help.getPath()} className="link color-brand-blue">contact your administrator</Link>.
                    </Trans>
                </div>
            }
            <LmidModalResultItem
                align={existingLmidData.cmmAsset ? "flex-start" : "center"}
                pictogram={{
                    src: pictogramAsset
                }}>
                <Flex>
                    <FlexItem shrink={0}>
                        <Text>{t('common.lmid')}:&nbsp;</Text>
                    </FlexItem>
                    <Lmid lmid={existingLmidData.lmid} labeled={existingLmidData.isLabeled} />
                </Flex>
                {!!existingLmidData.cmmAsset &&
                    <>
                        {!!existingLmidData.cmmAsset.description &&
                            <Flex>
                                <FlexItem shrink={0}>
                                    <Text>{t('common.description')}:&nbsp;</Text>
                                </FlexItem>
                                <ArticleDescription articleDescription={existingLmidData.cmmAsset.description} />
                            </Flex>
                        }
                        {!!existingLmidData.cmmAsset.drawingNumber && <Text>{t('asset.drawingNumber')}: {existingLmidData.cmmAsset.drawingNumber}</Text>}
                        {!!existingLmidData.cmmAsset.serialNumber && <Text>{t('asset.serialNumber')}: {existingLmidData.cmmAsset.serialNumber}</Text>}
                        {typeof existingLmidData.cmmAsset.constructionYear === "number" && <Text>{t('asset.yearOfConstruction')}: {existingLmidData.cmmAsset.constructionYear}</Text>}
                    </>
                }
            </LmidModalResultItem>
            <div className="mt-3">
                {!existingLmidData.cmmAsset &&
                    <Button
                        label={t('inventory.addAsset')}
                        onClick={handleNotUsedLmidSelect}
                    />
                }
                {!!existingLmidData.cmmAsset && isInternalArticle &&
                    <Grid columns="1fr 1fr"
                        gap="2.4rem">
                        <Button
                            label={t('lmidModal.noSelectDifferentArticle')}
                            onClick={onSelectArticleClick}
                            theme="secondary"
                        />
                        <Button
                            label={t('lmidModal.yesAddAsset')}
                            onClick={handleNotUsedLmidSelect}
                        />
                    </Grid>
                }
                {!!existingLmidData.cmmAsset && !isInternalArticle &&
                    <Button
                        theme="secondary"
                        label={t('asset.create.selectArticle.button')}
                        onClick={onSelectArticleClick}
                    />
                }
            </div>
        </div>
    )
}

export default LmidModalNotUsed
